import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext.js";
import { cognitoErrorCode, cognitoErrorMessage } from "../lib/auth.js";
import {
  AuthForm,
  AuthShell,
  ErrorBanner,
  SuccessBanner,
  fieldCls,
  labelCls,
  linkCls,
} from "../components/AuthShell.js";
import { Button } from "../components/ui/Button.js";

type Step = "request" | "confirm";

const PASSWORD_HINT =
  "At least 8 characters, with upper, lower, a number, and a symbol.";

export function ForgotPage() {
  const { forgotPassword, confirmForgotPassword } = useAuth();
  const navigate = useNavigate();

  const [step, setStep] = useState<Step>("request");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function handleRequest(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setInfo(null);
    try {
      await forgotPassword(email.trim());
      setInfo("If that email is registered, a reset code is on its way.");
      setStep("confirm");
    } catch (err) {
      // Cognito may still error for invalid email format, etc.
      setError(cognitoErrorMessage(err, "Could not start password reset."));
    } finally {
      setBusy(false);
    }
  }

  async function handleConfirm(e: FormEvent) {
    e.preventDefault();
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await confirmForgotPassword(email.trim(), code.trim(), password);
      navigate(`/login?email=${encodeURIComponent(email.trim())}`, { replace: true });
    } catch (err) {
      const c = cognitoErrorCode(err);
      if (c === "CodeMismatchException") {
        setError("That code is incorrect.");
      } else if (c === "ExpiredCodeException") {
        setError("That code has expired. Request a new one.");
      } else if (c === "InvalidPasswordException") {
        setError(cognitoErrorMessage(err, "Password does not meet requirements."));
      } else {
        setError(cognitoErrorMessage(err, "Could not reset password."));
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthShell
      title="Reset password"
      subtitle={
        step === "request"
          ? "We'll email you a reset code."
          : "Enter the code and choose a new password."
      }
    >
      {step === "request" ? (
        <AuthForm onSubmit={handleRequest}>
          <ErrorBanner message={error} />
          <SuccessBanner message={info} />
          <div>
            <label htmlFor="email" className={labelCls}>
              Email
            </label>
            <input
              id="email"
              type="email"
              autoComplete="email"
              required
              className={fieldCls}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <Button type="submit" fullWidth disabled={busy}>
            {busy ? "Sending…" : "Send reset code"}
          </Button>
          <p className="pt-1 text-center text-sm text-muted">
            <Link to="/login" className={linkCls}>
              Back to login
            </Link>
          </p>
        </AuthForm>
      ) : (
        <AuthForm onSubmit={handleConfirm}>
          <ErrorBanner message={error} />
          <SuccessBanner message={info} />
          <div>
            <label htmlFor="code" className={labelCls}>
              Reset code
            </label>
            <input
              id="code"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              required
              className={fieldCls}
              value={code}
              onChange={(e) => setCode(e.target.value)}
            />
          </div>
          <div>
            <label htmlFor="password" className={labelCls}>
              New password
            </label>
            <input
              id="password"
              type="password"
              autoComplete="new-password"
              required
              minLength={8}
              className={fieldCls}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <p className="mt-1 text-xs text-muted-sm">{PASSWORD_HINT}</p>
          </div>
          <div>
            <label htmlFor="confirm" className={labelCls}>
              Confirm password
            </label>
            <input
              id="confirm"
              type="password"
              autoComplete="new-password"
              required
              minLength={8}
              className={fieldCls}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
            />
          </div>
          <Button type="submit" fullWidth disabled={busy}>
            {busy ? "Saving…" : "Set new password"}
          </Button>
          <button
            type="button"
            className="w-full text-center text-sm text-muted underline-offset-2 hover:underline"
            onClick={() => {
              setStep("request");
              setError(null);
            }}
          >
            Resend code
          </button>
        </AuthForm>
      )}
    </AuthShell>
  );
}
